import {
  AppContext,
  useActivityTracker,
  GET_CONSTANTS,
  useProduct,
  COMMON_CONSTANTS
} from '@nile/components';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import ErrorPage from '@nile/components/src/Components/ErrorPage';
import {
  AccountClient,
  GET_ALL_ACTIVE_ADDONS,
  GET_ALL_ACTIVE_PLANS,
  GET_CUSTOMER_CARD_DETAILS,
  GET_CUSTOMER_SUBSCRIPTION
} from '@nile/graphql';
import envConfig from '@nile/home/src/Config/env';
import { Button } from '@superops/ui-design-system';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { UpgradePlan } from '@nile/banyan/src/Pages/Protected/Configuration/Pricing';
import ContactVendorModal from './ContactVendorButton';
import { restrictUpgradeOldPlans } from '@nile/banyan/src/Utils/pricingHelper';
import dayjs from 'dayjs';

const UpgradePlanButton = (props) => {
  const {
    secondaryButton = false,
    buttonSize,
    fontWeight,
    transparentBg,
    padding,
    fontSize,
    label,
    feature
  } = props;
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP)
  const [allPlans, setAllPlans] = useState([]);
  const [addOnList, setAddOnList] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  const [currency, setCurrency] = useState('USD');
  const [tenure, setTenure] = useState('YEARLY');
  const [locale, setLocale] = useState('en-US');

  const [openUpgradePlan, setOpenUpgradePlan] = useState(false);
  const [isTieredModel, setIsTieredModel] = useState(false)

  const appContext = useContext(AppContext);
  const {
    user,
    currentLanguage,
    isSuperopsUser,
    isTrialUser,
    currentProduct
  } = appContext;

  const oldPlanUpgradeHandler = () => {
    const userMeta = {
      email: user.email,
      name: user.name,
      userId: user.userId,
      subscriptionStatus: user.subscriptionStatus,
      currentPlan: currentSubscription?.currentPlanPrice?.plan?.planName,
      chargeBeePlanId:
        currentSubscription?.currentPlanPrice?.plan?.chargeBeePlanId,
      billingPeriod: currentSubscription?.billingPeriod,
      licenseCount: currentSubscription?.licenseCount,
      addOnsCount: currentSubscription?.subscribedAddons?.length,
      nextBillingDateTime: dayjs(
        currentSubscription?.nextBillingDateTime
      ).format('MMM DD, YYYY')
    };
    window.Intercom('trackEvent', 'upgrade-from-old-plan', userMeta);
  };

  const getAllPlans = () => {
    AccountClient.query({
      query: GET_ALL_ACTIVE_PLANS,
      fetchPolicy: 'network-only'
    }).then((res) => {
      if (res?.data?.getAllActivePlans) {
        setAllPlans(res.data.getAllActivePlans);
      }
    });
  };
  const getCountryList = async () => {
    try {
      const country = await GET_CONSTANTS('country');
      return country;
    } catch {
      // error
    }
  };

  const getAllAddons = () => {
    AccountClient.query({
      query: GET_ALL_ACTIVE_ADDONS,
      fetchPolicy: 'network-only'
    }).then((res) => {
      if (res?.data?.getAllActiveAddons) {
        setAddOnList(res.data.getAllActiveAddons);
      }
    });
  };

  const getCustomerSubscription = () => {
    AccountClient.query({
      query: GET_CUSTOMER_SUBSCRIPTION,
      fetchPolicy: 'network-only'
    })
      .then(async (res) => {
        if (res?.data?.getCustomerSubscription) {
          setCurrentSubscription(res.data.getCustomerSubscription);
          const isTieredModel =
            res.data.getCustomerSubscription?.currentPlanPrice?.pricingModel === 'TIERED'
          setIsTieredModel(isTieredModel)
          setTenure(
            res.data.getCustomerSubscription?.currentPlanPrice?.duration ||
              'YEARLY'
          );
          if (
            res.data.getCustomerSubscription?.currentPlanPrice?.currency
              ?.currencyCode
          ) {
            const curr =
              res.data.getCustomerSubscription.currentPlanPrice.currency;
            setCurrency(curr.currencyCode || 'USD');
            const countryList = await getCountryList();
            setLocale(
              countryList?.filter(
                (country) => country.currency === curr.currencyCode
              )?.[0]?.isoCode
                ? `${currentLanguage.value || 'en'}-${
                    countryList?.filter(
                      (country) => country.currency === curr.currencyCode
                    )?.[0]?.isoCode
                  }`
                : 'en-US'
            );
          }
        }
      })
      .finally(() => {});
  };

  useEffect(() => {
    getAllPlans();
    getAllAddons();
    if (!isTrialUser) {
      getCustomerSubscription();
    }
  }, []);

  const renderMessage = !user?.subscriptionStatus;

  if (renderMessage) {
    return (
      <ErrorPage
        imageURL={`url(${envConfig.imageRoot}svg/PageNotFound.svg)`}
        text="Purchase plan"
        description="You are in trial period. Please make a purchase"
        showBtn
      />
    );
  }

  return (
    <Wrapper
      className={!secondaryButton && 'upgrade-primary-btn'}
      buttonSize={buttonSize}
      fontWeight={fontWeight}
      transparentBg={transparentBg}
      padding={padding}
      fontSize={fontSize}
    >
      <Button
        onClick={() => {
          if (
            restrictUpgradeOldPlans.includes(
              currentSubscription?.currentPlanPrice?.plan?.chargeBeePlanId
            ) ||
            (feature === 'MDM' && isMSPProduct) // In-product upgrade available only IT.
          ) {
            oldPlanUpgradeHandler();
          } else {
            useActivityTracker({
              event: `Inproduct purchase - ${props.feature || 'Timesheet'}`
            });
            setOpenUpgradePlan(true);
          }
        }}
      >
        <FormattedMessage id={label || 'admin.billing.upgradeplan'} />
      </Button>
      {isSuperopsUser ? (
        <UpgradePlan
          active={openUpgradePlan}
          handleClose={setOpenUpgradePlan}
          subscription={currentSubscription}
          getCustomerSubscription={getCustomerSubscription}
          plans={allPlans}
          tenure={tenure}
          currency={currency}
          locale={locale}
          addOnList={addOnList}
          isTieredModel={isTieredModel}
        />
      ) : (
        <ContactVendorModal
          active={openUpgradePlan}
          handleClose={setOpenUpgradePlan}
        />
      )}
    </Wrapper>
  );
};

const fontWeightMap = {
  Medium: 500,
  SemiBold: 600,
  Bold: 700
};
const Wrapper = styled.div`
  &.upgrade-primary-btn {
    .Superops-Button {
      box-shadow: none;
      border-radius: 50px;
      background: ${(props) =>
        props?.transparentBg ? 'transparent !important' : '#f05 !important'};
      color: #fff;
      min-height: ${(props) => props?.buttonSize === 'small' && 'unset'};
      padding: ${(props) =>
        props?.padding === 'large'
          ? '18px 32px '
          : props?.buttonSize === 'small' && '4px 20px'};
      .Superops-Button__Text {
        font-size: ${(props) =>
          props?.fontSize === 'medium'
            ? '16px'
            : props?.buttonSize === 'small' && '12px'};
        font-weight: ${(props) =>
          props?.fontWeight && fontWeightMap?.[props?.fontWeight]
            ? fontWeightMap?.[props?.fontWeight]
            : 400};
      }
    }
  }
`;

export default UpgradePlanButton;
